import React, { useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Content,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@patternfly/react-core';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAdminGpuActions } from '../../../hooks/useAdminVerdaServers';
import type { VerdaGpuServer } from '../../../types/models/Verda';
import { NoteField, TypedConfirmField, isValidNote, typedMatches } from './VerdaGpuParts';

interface VerdaMarkNotCreatedModalProps {
  server: VerdaGpuServer;
  onClose: () => void;
  /** Something may have changed: the detail refetches. */
  onChanged: () => void;
}

/**
 * `POST /admin/verda/servers/{id}/mark-not-created` — for an unconfirmed start that staff verified in
 * the Verda console never existed: every hour charged is refunded and the row becomes permanently
 * `failed`. Typed hostname, a required note and an explicit console-check acknowledgement. Mounted
 * only while open.
 */
function VerdaMarkNotCreatedModal({ server, onClose, onChanged }: VerdaMarkNotCreatedModalProps) {
  const { markNotCreated, busy } = useAdminGpuActions();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [typed, setTyped] = useState('');
  const [note, setNote] = useState('');
  const [checked, setChecked] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [noteError, setNoteError] = useState<string | null>(null);

  const canSubmit = !busy && checked && isValidNote(note) && typedMatches(typed, server.hostname);

  const handleSubmit = async () => {
    setError(null);
    setNoteError(null);
    const outcome = await markNotCreated(server.id, note.trim());
    if (outcome.status === 'error') {
      setError(outcome.message);
      setNoteError(outcome.fieldErrors.note ?? null);
      addNotification({
        title: `Could not mark ${server.hostname} as not created`,
        srTitle: 'Error alert',
        description: outcome.message,
        variant: 'danger',
      });
      setDrawerOpen(true);
      return;
    }
    const { refunded, refund_failed: refundFailed } = outcome.data;
    addNotification({
      title: `${server.hostname} marked as not created`,
      srTitle: refundFailed > 0 ? 'Warning alert' : 'Success alert',
      description:
        refundFailed > 0
          ? `${refunded} hour(s) refunded; ${refundFailed} refund(s) failed and are retried by the meter's refund sweep.`
          : `${refunded} hour(s) refunded to the customer's balance. The server is now failed.`,
      variant: refundFailed > 0 ? 'warning' : 'success',
    });
    setDrawerOpen(true);
    onChanged();
    onClose();
  };

  return (
    <Modal variant="small" isOpen onClose={busy ? undefined : onClose} aria-labelledby="verda-not-created-title">
      <ModalHeader
        title="Mark as not created"
        labelId="verda-not-created-title"
        titleIconVariant="warning"
        description={server.hostname}
      />
      <ModalBody>
        {error !== null && (
          <Alert variant="danger" isInline title="Could not mark the server as not created" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content component="p">
          This refunds every hour charged for <strong>{server.hostname}</strong> to the customer&apos;s balance and
          marks it permanently failed. The backend re-reads the Verda listing first and refuses if an instance carries
          this server&apos;s token (the meter will adopt it).
        </Content>
        <Alert
          variant="warning"
          isInline
          title="If an instance turns up later, it bills Ancalagon"
          className="pf-v6-u-mb-md"
        >
          The meter deletes it only while the destroy switch is on; otherwise it raises a finding and the instance must
          be deleted in the Verda console. Search the console by the tags <code>mgr-ns={server.namespace}</code> and{' '}
          <code>mgr-server={server.correlation_token}</code>.
        </Alert>
        <Form onSubmit={(event) => event.preventDefault()}>
          <Checkbox
            id="verda-not-created-checked"
            label="I checked the provider console: no instance exists"
            isChecked={checked}
            onChange={(_event, value) => setChecked(value)}
          />
          <NoteField
            id="verda-not-created-note"
            label="Note"
            value={note}
            onChange={setNote}
            error={noteError}
            hint="What you checked, and why nothing exists (kept with the resolution)."
          />
          <TypedConfirmField
            id="verda-not-created-confirm"
            expected={server.hostname}
            value={typed}
            onChange={setTyped}
            hint="This refunds the customer and cannot be undone, so the hostname is typed out in full."
          />
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleSubmit()} isLoading={busy} isDisabled={!canSubmit}>
          Mark as not created
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={busy}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default VerdaMarkNotCreatedModal;
